// src/pages/AdminUserBalances.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  ArrowLeft,
  User,
  Wallet,
  Coins,
  Eye,
  X,
  AlertCircle,
  Loader2,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import Swal from 'sweetalert2';
import persian_fa from 'react-date-object/locales/persian_fa';
import { FaCalendarAlt, FaEye } from 'react-icons/fa';
import persian from 'react-date-object/calendars/persian';

import DateObject from 'react-date-object';
const showAlert = (message, type) => {
  Swal.fire({
    text: message,
    icon: type,
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    customClass: { popup: 'font-vazir' },
  });
};

const formatNumber = (value, isGold = false) => {
  if (!value || isNaN(parseFloat(value))) return '0';
  const num = parseFloat(value);
  return isGold
    ? num.toLocaleString('fa-IR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    : num.toLocaleString('fa-IR');
};

export default function AdminUserBalances() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    minRial: '',
    maxRial: '',
    minGold: '',
    maxGold: '',
  });
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  // بستن مودال با کلیک خارج
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowTransactions(false);
      }
    };
    if (showTransactions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTransactions]);

  // رفرش توکن
  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  // دریافت کاربران
  const fetchUsers = async (appliedFilters) => {
    setLoading(true);
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/admin/users-balances', {
        headers: { Authorization: `Bearer ${newToken}` },
        params: appliedFilters,
      });
      setUsers(res.data);
      setError('');
    } catch (err) {
      const msg = err.response?.data?.message || 'خطا در دریافت کاربران';
      showAlert(msg, 'error');
      setError(msg);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // دریافت تراکنش‌های کاربر
  const fetchUserTransactions = async (userId) => {
    setLoading(true);
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get(`/api/admin/user-transactions/${userId}`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      const txWithBalance = calculateRunningBalances(res.data);
      setTransactions(txWithBalance);
      setShowTransactions(true);
    } catch (err) {
      showAlert('خطا در دریافت تراکنش‌ها', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchUsers(filters);
  }, [token, navigate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setFilters(tempFilters);
    fetchUsers(tempFilters);
  };

  const handleResetFilters = () => {
    const reset = { search: '', minRial: '', maxRial: '', minGold: '', maxGold: '' };
    setTempFilters(reset);
    setFilters(reset);
    fetchUsers(reset);
  };

  // محاسبه مانده جاری
  const calculateRunningBalances = (txs) => {
    let rial = 0, gold = 0;
    return txs.map(tx => {
      const cr = parseFloat(tx.credit_rial) || 0;
      const dr = parseFloat(tx.debit_rial) || 0;
      const cg = parseFloat(tx.credit_gold) || 0;
      const dg = parseFloat(tx.debit_gold) || 0;

      rial += cr - dr;
      gold += cg - dg;

      return { ...tx, running_rial: rial, running_gold: gold };
    });
  };

  const getTypeText = (type) => {
    const map = {
      wallet_deposit: 'شارژ کیف پول',
      gold_deposit:  'شارژ کیف طلا',
      wallet_withdraw: 'برداشت کیف پول',
      buy: 'خرید طلا',
      sell: 'فروش طلا',
      gold_request: 'درخواست آبشده',
      reward_referral:'هدیه معرف'
    };
    return map[type] || type;
  };

  const getStatusText = (status) => {
    const map = {
      approved: 'تأیید شده',
      ready: 'آماده تحویل',
      delivered: 'تحویل داده شده',
      rejected: 'رد شده',
      deposited: 'واریز شده',
    };
    return map[status] || status;
  };

  const formatPersianDate = (date) => {
    try {
      return new DateObject({ date: new Date(date), calendar: persian, locale: persian_fa })
        .format('YYYY/MM/DD HH:mm');
    } catch {
      return '—';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-7xl mx-auto">
        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-indigo-600 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          برگشت
        </motion.button>

        {/* هدر */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center text-white shadow-xl">
              <Wallet className="w-9 h-9" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">تراز کاربران</h2>
          <p className="text-gray-600 mt-2">مشاهده مانده ریالی و طلایی کاربران</p>
        </motion.div>

        {/* خطا */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3"
            >
              <AlertCircle className="w-6 h-6" />
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* فیلترها */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 border border-white/30"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <Filter className="w-6 h-6 text-indigo-600" />
            فیلترهای پیشرفته
          </h3>

          <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {/* جستجو */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">جستجو</label>
              <input
                type="text"
                name="search"
                value={tempFilters.search}
                onChange={handleFilterChange}
                placeholder="نام، شماره، شناسه..."
                className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
              />
            </div>

            {/* مانده ریالی */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداقل ریال</label>
                <input
                  type="number"
                  name="minRial"
                  value={tempFilters.minRial}
                  onChange={handleFilterChange}
                  placeholder="ریال"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداکثر ریال</label>
                <input
                  type="number"
                  name="maxRial"
                  value={tempFilters.maxRial}
                  onChange={handleFilterChange}
                  placeholder="ریال"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>

            {/* مانده طلایی */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداقل طلا</label>
                <input
                  type="number"
                  step="0.001"
                  name="minGold"
                  value={tempFilters.minGold}
                  onChange={handleFilterChange}
                  placeholder="گرم"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">حداکثر طلا</label>
                <input
                  type="number"
                  step="0.001"
                  name="maxGold"
                  value={tempFilters.maxGold}
                  onChange={handleFilterChange}
                  placeholder="گرم"
                  className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>

            {/* دکمه‌ها */}
            <div className="flex gap-3 items-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Filter className="w-5 h-5" />
                اعمال فیلتر
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={handleResetFilters}
                className="flex-1 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" />
                بازنشانی
              </motion.button>
            </div>
          </form>
        </motion.div>

        {/* لیست کاربران */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 mt-8 border border-white/30 overflow-x-auto"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800">لیست کاربران</h3>
            {loading && <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />}
          </div>

          {users.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">هیچ کاربری یافت نشد</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 text-gray-700">
                    <th className="p-4 text-right font-bold">شناسه</th>
                    <th className="p-4 text-right font-bold">نام</th>
                    <th className="p-4 text-right font-bold">شماره</th>
                    <th className="p-4 text-right font-bold">مانده ریالی</th>
                    <th className="p-4 text-right font-bold">مانده طلایی</th>
                    <th className="p-4 text-right font-bold">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-gray-100 hover:bg-indigo-50/50 transition-all duration-200"
                    >
                      <td className="p-4 font-mono text-indigo-600">#{user.id}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-gray-500" />
                          <span className="font-semibold">{user.first_name} {user.last_name}</span>
                        </div>
                      </td>
                      <td className="p-4 font-mono text-gray-700">{user.phone_number}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Wallet className="w-5 h-5 text-blue-600" />
                          <span className="font-mono text-blue-700">{formatNumber(user.balance_rial)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Coins className="w-5 h-5 text-yellow-600" />
                          <span className="font-mono text-yellow-700">{formatNumber(user.balance_gold, true)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedUser(user);
                            fetchUserTransactions(user.id);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          <Eye className="w-5 h-5" />
                          مشاهده تراکنش‌ها
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* مودال تراکنش‌ها */}
        <AnimatePresence>
          {showTransactions && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            >
              <motion.div
                ref={modalRef}
                initial={{ scale: 0.9, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 50 }}
                className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden border border-white/30"
              >
                <div className="sticky top-0 bg-white/90 backdrop-blur-xl border-b border-gray-200 p-6 flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">
                      تراکنش‌های {selectedUser.first_name} {selectedUser.last_name}
                    </h3>
                    <p className="text-sm text-gray-600">شناسه: {selectedUser.id} | شماره: {selectedUser.phone_number}</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowTransactions(false)}
                    className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <X className="w-6 h-6" />
                  </motion.button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 text-gray-700 sticky top-0">
                          <th className="p-3 text-right font-bold">سند</th>
                          <th className="p-3 text-right font-bold">نوع</th>
                          <th className="p-3 text-right font-bold">وضعیت</th>
                          <th className="p-3 text-right font-bold">شرح</th>
                          <th className="p-3 text-right font-bold">قیمت/گرم</th>
                          <th className="p-3 text-right font-bold">بدهکار طلا</th>
                          <th className="p-3 text-right font-bold">بستانکار طلا</th>
                          <th className="p-3 text-right font-bold">مانده طلا</th>
                          <th className="p-3 text-right font-bold">بدهکار ریال</th>
                          <th className="p-3 text-right font-bold">بستانکار ریال</th>
                          <th className="p-3 text-right font-bold">مانده ریال</th>
                          <th className="p-3 text-right font-bold">تاریخ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx, i) => (
                          <motion.tr
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className="border-b border-gray-100 hover:bg-indigo-50/50 transition-all duration-200"
                          >
                            <td className="p-3 font-mono text-indigo-600">#{tx.id}</td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                                tx.type.includes('deposit') ? 'bg-green-100 text-green-700' :
                                tx.type.includes('reward_referral') ? 'bg-green-100 text-green-700' :
                                tx.type.includes('withdraw') ? 'bg-red-100 text-red-700' :
                                tx.type === 'buy' ? 'bg-blue-100 text-blue-700' :
                                tx.type === 'sell' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {tx.type.includes('deposit') && <TrendingUp className="w-3 h-3" />}
                                {tx.type.includes('withdraw') && <TrendingDown className="w-3 h-3" />}
                                {getTypeText(tx.type)}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                                tx.status === 'approved' || tx.status === 'delivered' || tx.status === 'deposited'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : tx.status === 'rejected' ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {getStatusText(tx.status)}
                              </span>
                            </td>
                            <td className="p-3 text-gray-700">{tx.description || '—'}</td>
                            <td className="p-3 font-mono">{tx.price_per_gram ? formatNumber(tx.price_per_gram) : '—'}</td>
                            <td className="p-3 font-mono text-red-600">{tx.debit_gold ? formatNumber(tx.debit_gold, true) : '—'}</td>
                            <td className="p-3 font-mono text-green-600">{tx.credit_gold ? formatNumber(tx.credit_gold, true) : '—'}</td>
                            <td className="p-3 font-mono font-bold text-yellow-700">{formatNumber(tx.running_gold, true)}</td>
                            <td className="p-3 font-mono text-red-600">{tx.debit_rial ? formatNumber(tx.debit_rial) : '—'}</td>
                            <td className="p-3 font-mono text-green-600">{tx.credit_rial ? formatNumber(tx.credit_rial) : '—'}</td>
                            <td className="p-3 font-mono font-bold text-blue-700">{formatNumber(tx.running_rial)}</td>
                            <td className="p-3 text-gray-600">{formatPersianDate(tx.created_at)}</td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* جمع کل موجودی کاربران - نسخه کوچک و شیک */}
        {users.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 mb-6 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-indigo-100"
          >
            <h3 className="text-lg font-bold text-gray-800 text-center mb-4 flex items-center justify-center gap-2">
              <Wallet className="w-6 h-6 text-indigo-600" />
              جمع کل موجودی کاربران
              <Coins className="w-6 h-6 text-yellow-600" />
            </h3>

            <div className="grid grid-cols-2 gap-6 text-center">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-gray-600 mb-1">موجودی ریالی کل</p>
                <p className="text-2xl font-bold font-mono text-blue-700">
                  {formatNumber(
                    users.reduce((sum, u) => sum + parseFloat(u.balance_rial || 0), 0)
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">تومان</p>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-200">
                <p className="text-sm text-gray-600 mb-1">موجودی طلایی کل</p>
                <p className="text-2xl font-bold font-mono text-amber-700">
                  {formatNumber(
                    users.reduce((sum, u) => sum + parseFloat(u.balance_gold || 0), 0),
                    true
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">گرم</p>
              </div>
            </div>

            <p className="text-center text-xs text-gray-500 mt-4">
              تعداد کاربران نمایش داده شده: {users.length} نفر
            </p>
          </motion.div>
        )}
        {/* فوتر */}
        <div className="text-center mt-10 text-xs text-gray-500">
          <p>© ۱۴۰۴ - ESF.Gold | پنل مدیریت</p>
        </div>
      </div>

      <style jsx>{`
        .font-vazir { font-family: 'Vazir', sans-serif !important; }
      `}</style>
    </div>
  );
}